import axios from "axios";
import { useSnackbar } from "notistack";
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SignInCredentials } from "../@types/contexts/Auth/AuthContext";
import type { User } from "../@types/services/User";
import { FullScreenLoading } from "../components/molecules/FullScreenLoading";
import { config } from "../config";
import { AuthContext } from "../contexts/Auth/AuthContext";
import { api } from "../services/api";

// Type definitions for API responses
interface AuthResponse {
  data: User;
  success: boolean;
}

interface SessionResponse {
  success: boolean;
}

interface ErrorResponse {
  status: boolean;
  errors: string[];
  error: string;
}

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  // State management
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const isAuthenticated = useMemo(() => {
    return !!user;
  }, [user]);

  // Refs to prevent double execution and race conditions
  const isRefreshingRef = useRef(false);
  const isSigningOutRef = useRef(false);
  const initializedRef = useRef(false);

  const signOut = useCallback(
    async (isManualLogout = false) => {
      // Prevent multiple simultaneous signOut calls
      if (isSigningOutRef.current) {
        return;
      }

      isSigningOutRef.current = true;

      // Clear sessions values that need to be reset in each sign in
      sessionStorage.removeItem("announcementsModalViewed");

      try {
        // Sign out from server
        await fetch("/users/sign_out", {
          headers: { "content-type": "application/json" },
          method: "DELETE",
        }).catch((fetchError) => {
          console.error("Sign out request failed:", fetchError);
        });

        setUser(null);
        setLoading(false);

        // Only show success message for manual logout
        if (isManualLogout) {
          enqueueSnackbar("Signed out successfully", { variant: "info" });
        }
      } finally {
        isSigningOutRef.current = false;
      }
    },
    [enqueueSnackbar],
  );

  const signIn = useCallback(
    async ({ email, password, token }: SignInCredentials) => {
      try {
        setLoading(true);

        // Clear sessions values that need to be reset in each sign in
        sessionStorage.removeItem("announcementsModalViewed");

        // Prepare form data for sign in
        const bodyFormData = new FormData();
        bodyFormData.append("user[login]", email);
        bodyFormData.append("user[password]", password);
        bodyFormData.append("g-recaptcha-response", token);

        // Sign in request
        await axios.post("/users/sign_in", bodyFormData, {
          headers: {
            accept: "application/json",
            "content-type": "multipart/form-data",
          },
          xsrfCookieName: config.csrfTokenCookieName,
          xsrfHeaderName: "x-csrf-token",
        });

        // Validate token and get user data
        const response = await api.get<AuthResponse>(config.auth.validateToken);

        if (response.data.success && response.data.data) {
          setUser(response.data.data);
          enqueueSnackbar("Signed in successfully", { variant: "success" });
        } else {
          throw new Error("Invalid authentication response");
        }
      } catch (axiosError) {
        if (axios.isAxiosError(axiosError)) {
          const errorData = axiosError.response?.data as
            | ErrorResponse
            | undefined;

          // Handle specific error messages
          if (errorData?.errors?.length) {
            errorData.errors.forEach((message) => {
              enqueueSnackbar(message, { variant: "error" });
            });
          } else if (errorData?.error) {
            enqueueSnackbar(errorData.error, { variant: "error" });
          } else {
            enqueueSnackbar("Sign in failed. Please try again.", {
              variant: "error",
            });
          }
        } else {
          enqueueSnackbar("An unexpected error occurred", { variant: "error" });
        }

        throw axiosError;
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  const refreshUser = useCallback(
    async (forceRefresh = false) => {
      // Prevent multiple simultaneous refresh calls
      if (isRefreshingRef.current) {
        return;
      }

      // Skip if user exists and not forcing refresh
      if (user && !forceRefresh) {
        return;
      }

      isRefreshingRef.current = true;

      try {
        // Check session validity
        const sessionResponse = await axios.get<SessionResponse>(
          "/user/session_is_valid",
        );
        const isSessionValid = sessionResponse.data.success;

        // Validate token and get user data
        const authResponse = await api.get<AuthResponse>(
          config.auth.validateToken,
        );
        const isTokenValid = authResponse.data.success;

        if (!isSessionValid || !isTokenValid) {
          // Session or token invalid - automatic sign out
          setUser(null);
          return;
        }

        // Update user data if valid
        if (authResponse.data.data) {
          setUser(authResponse.data.data);
        } else {
          setUser(null);
        }
      } catch {
        setUser(null);
      } finally {
        isRefreshingRef.current = false;
        setLoading(false);
      }
    },
    [user],
  );

  // Initialize user on mount - only run once
  // biome-ignore lint/correctness/useExhaustiveDependencies: Empty dependency array - only run on mount, refreshUser is stable
  useEffect(() => {
    if (initializedRef.current) {
      return undefined;
    }

    initializedRef.current = true;
    let isCancelled = false;

    const initializeAuth = async (): Promise<void> => {
      if (!isCancelled) {
        await refreshUser();
      }
    };

    initializeAuth();

    return () => {
      isCancelled = true;
    };
  }, []); // Empty dependency array - only run on mount, refreshUser is stable

  // Memoized context value
  const providerValues = useMemo(
    () => ({
      isAuthenticated,
      loading,
      refreshUser,
      signIn,
      signOut,
      user,
    }),
    [isAuthenticated, loading, refreshUser, signIn, signOut, user],
  );

  if (loading) {
    return <FullScreenLoading />;
  }

  return (
    <AuthContext.Provider value={providerValues}>
      {children}
    </AuthContext.Provider>
  );
};
